import { apiClient } from './apiClient';

export interface WhatsAppConversation {
  id: string;
  filename: string;
  status: 'uploaded' | 'anonymized' | 'approved' | 'extracted' | 'indexed' | 'processed' | 'discarded';
  uploaded_at: string;
  anonymized_at?: string;
  approved_at?: string;
  extracted_at?: string;
  indexed_at?: string;
  processed_at?: string;
  file_size: number;
  message_count?: number;
  admin_notes?: string;
  content?: string;
}

export interface ExtractedKnowledgeResponse {
  id: string;
  filename: string;
  knowledge_filename: string;
  status: WhatsAppConversation['status'];
  content: string;
}

export interface ConversationListResponse {
  conversations: WhatsAppConversation[];
  count: number;
}

export interface UploadResponse {
  message: string;
  conversations: WhatsAppConversation[];
}

export interface ProcessResponse {
  message: string;
  count: number;
  errors?: string[];
}

class WhatsAppService {
  private baseUrl = '/ingestion/whatsapp';

  /**
   * Upload WhatsApp conversation files
   */
  async uploadConversations(files: File[]): Promise<UploadResponse> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await apiClient.post(`${this.baseUrl}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });

    return response.data;
  }

  /**
   * Anonymize uploaded conversations
   */
  async anonymizeConversations(): Promise<ProcessResponse> {
    const response = await apiClient.post(`${this.baseUrl}/anonymize`);
    return response.data;
  }

  /**
   * List all conversations
   */
  async listConversations(status?: string): Promise<ConversationListResponse> {
    const response = await apiClient.get(`${this.baseUrl}/conversations`, {
      params: status ? { status } : undefined,
    });
    return response.data;
  }

  /**
   * Get specific conversation with content
   */
  async getConversation(id: string): Promise<WhatsAppConversation> {
    const response = await apiClient.get(`${this.baseUrl}/conversations/${id}`);
    return response.data;
  }

  /**
   * Get extracted knowledge text for a specific conversation
   */
  async getExtractedConversation(id: string): Promise<ExtractedKnowledgeResponse> {
    const response = await apiClient.get(`${this.baseUrl}/conversations/${id}/extracted`);
    return response.data;
  }

  /**
   * Update conversation content
   */
  async updateConversation(
    id: string,
    content: string,
    adminNotes?: string
  ): Promise<{ message: string; conversation: WhatsAppConversation }> {
    const response = await apiClient.put(`${this.baseUrl}/conversations/${id}`, {
      content,
      admin_notes: adminNotes,
    });
    return response.data;
  }

  /**
   * Approve conversation for knowledge extraction
   */
  async approveConversation(id: string): Promise<{ message: string; conversation: WhatsAppConversation }> {
    const response = await apiClient.post(`${this.baseUrl}/conversations/${id}/approve`);
    return response.data;
  }

  /**
   * Discard conversation
   */
  async discardConversation(id: string): Promise<{ message: string; conversation: WhatsAppConversation }> {
    const response = await apiClient.post(`${this.baseUrl}/conversations/${id}/discard`);
    return response.data;
  }

  /**
   * Permanently delete a conversation and its files
   */
  async deleteConversation(id: string): Promise<{ message: string; id: string }> {
    const response = await apiClient.delete(`${this.baseUrl}/conversations/${id}`);
    return response.data;
  }

  /**
   * Start knowledge extraction in background (returns immediately)
   */
  async extractKnowledge(): Promise<ProcessResponse> {
    const response = await apiClient.post(`${this.baseUrl}/extract`, null, {
      timeout: 15000,
    });
    return response.data;
  }

  /**
   * Poll extraction progress
   */
  async getExtractionStatus(): Promise<{
    running: boolean;
    total: number;
    processed: number;
    current_file: string | null;
    current_chunk: number;
    total_chunks: number;
    errors: string[];
    last_result: string | null;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/extract-status`);
    return response.data;
  }

  /**
   * List extracted conversations pending RAG indexing
   */
  async getPendingIndexing(): Promise<ConversationListResponse> {
    const response = await apiClient.get(`${this.baseUrl}/pending-indexing`);
    return response.data;
  }

  /**
   * Mark extracted conversations as indexed after successful RAG build
   */
  async markIndexed(): Promise<ProcessResponse> {
    const response = await apiClient.post(`${this.baseUrl}/mark-indexed`);
    return response.data;
  }

  /**
   * Run full pipeline: anonymize → extract → process
   */
  async processAll(): Promise<ProcessResponse & { anonymized: number; ready_for_raptor: number }> {
    const response = await apiClient.post(`${this.baseUrl}/process`, null, {
      timeout: 300000,
    });
    return response.data;
  }

  /**
   * Validate WhatsApp file
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size (10MB max for WhatsApp exports)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Arquivo muito grande. Tamanho máximo: 10MB.',
      };
    }

    // Check file type
    if (!file.name.toLowerCase().endsWith('.txt')) {
      return {
        valid: false,
        error: 'Tipo de arquivo inválido. Apenas arquivos .txt são permitidos.',
      };
    }

    return { valid: true };
  }
}

export const whatsappService = new WhatsAppService();
